import React, { useState, useEffect, useCallback, useRef } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useAuthContext } from "../contexts/AuthContext";
import { authenticatedFetch } from "../utils/authenticatedFetch";
import styles from "./AdminEmail.module.css";

interface Campaign {
    id: string;
    subject: string;
    body_markdown: string;
    body_html: string | null;
    filters: Record<string, string>;
    recipient_count: number;
    sent_count: number;
    failed_count: number;
    status: "draft" | "sending" | "completed" | "partial" | "failed";
    sent_by: string;
    created_at: string;
    completed_at: string | null;
}

interface RecipientInfo {
    count: number;
    sample: string[];
}

const STATUS_COLORS: Record<string, string> = {
    draft: "#937663",
    sending: "#b45309",
    completed: "#15803d",
    partial: "#c2410c",
    failed: "#ba5656",
};

const SUBSCRIPTION_LABELS: Record<string, string> = {
    all: "All",
    active_premium: "Active Premium",
    active_premium_plus: "Active Premium+",
    active_any: "Any Active",
    none: "No Subscription",
    canceled: "Canceled",
};

const ACTIVITY_LABELS: Record<string, string> = {
    all: "All",
    "7d": "Active in last 7 days",
    "30d": "Active in last 30 days",
    "90d": "Active in last 90 days",
    inactive: "Inactive (90+ days)",
};

const AdminEmail: React.FC = () => {
    const { user } = useAuthContext();
    const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5001";

    // Tab state
    const [activeTab, setActiveTab] = useState<"compose" | "history">("compose");

    // Compose form state
    const [subject, setSubject] = useState("");
    const [bodyMarkdown, setBodyMarkdown] = useState("");
    const [subscriptionStatus, setSubscriptionStatus] = useState("all");
    const [activityRecency, setActivityRecency] = useState("all");
    const [signupFrom, setSignupFrom] = useState("");
    const [signupTo, setSignupTo] = useState("");
    const [recipientInfo, setRecipientInfo] = useState<RecipientInfo | null>(null);
    const [loadingCount, setLoadingCount] = useState(false);
    const [sending, setSending] = useState(false);
    const [toast, setToast] = useState<{ type: "success" | "error"; message: string } | null>(null);

    // Modals
    const [showPreview, setShowPreview] = useState(false);
    const [previewHtml, setPreviewHtml] = useState("");
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [showProgress, setShowProgress] = useState(false);
    const [activeCampaignId, setActiveCampaignId] = useState<string | null>(null);
    const [activeCampaign, setActiveCampaign] = useState<Campaign | null>(null);
    const [sendingTest, setSendingTest] = useState(false);

    // History state
    const [campaigns, setCampaigns] = useState<Campaign[]>([]);
    const [historyPage, setHistoryPage] = useState(1);
    const [historyPages, setHistoryPages] = useState(1);
    const [historyTotal, setHistoryTotal] = useState(0);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [expandedCampaign, setExpandedCampaign] = useState<string | null>(null);

    // Debounce ref
    const countTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Poll ref
    const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

    // =========================================================================
    // Recipient Count (debounced)
    // =========================================================================
    const fetchRecipientCount = useCallback(async () => {
        if (!user?.id) return;
        setLoadingCount(true);
        try {
            const params = new URLSearchParams({
                user_id: user.id,
                subscription_status: subscriptionStatus,
                activity_recency: activityRecency,
                signup_date_from: signupFrom,
                signup_date_to: signupTo,
            });
            const res = await authenticatedFetch(`${apiUrl}/api/admin/email/recipients/count?${params}`);
            if (res.ok) {
                const data = await res.json();
                setRecipientInfo(data);
            }
        } catch (err) {
            console.error("Failed to fetch recipient count", err);
        } finally {
            setLoadingCount(false);
        }
    }, [user?.id, apiUrl, subscriptionStatus, activityRecency, signupFrom, signupTo]);

    useEffect(() => {
        if (countTimerRef.current) clearTimeout(countTimerRef.current);
        countTimerRef.current = setTimeout(() => {
            fetchRecipientCount();
        }, 500);
        return () => {
            if (countTimerRef.current) clearTimeout(countTimerRef.current);
        };
    }, [fetchRecipientCount]);

    // =========================================================================
    // Preview
    // =========================================================================
    const handlePreview = async () => {
        if (!subject.trim() || !bodyMarkdown.trim()) return;
        setLoadingPreview(true);
        try {
            const res = await authenticatedFetch(`${apiUrl}/api/admin/email/preview`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    user_id: user?.id,
                    subject: subject.trim(),
                    body_markdown: bodyMarkdown.trim(),
                }),
            });
            if (res.ok) {
                const data = await res.json();
                setPreviewHtml(data.html);
                setShowPreview(true);
            } else {
                const err = await res.json();
                setToast({ type: "error", message: err.error || "Preview failed" });
            }
        } catch (err) {
            setToast({ type: "error", message: "Failed to generate preview" });
        } finally {
            setLoadingPreview(false);
        }
    };

    // =========================================================================
    // Send Test
    // =========================================================================
    const handleSendTest = async () => {
        if (!subject.trim() || !bodyMarkdown.trim()) return;
        setSendingTest(true);
        try {
            const res = await authenticatedFetch(`${apiUrl}/api/admin/email/send-test`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    user_id: user?.id,
                    subject: subject.trim(),
                    body_markdown: bodyMarkdown.trim(),
                }),
            });
            if (res.ok) {
                setToast({ type: "success", message: "Test email sent to your inbox" });
            } else {
                const err = await res.json();
                setToast({ type: "error", message: err.error || "Test send failed" });
            }
        } catch (err) {
            setToast({ type: "error", message: "Failed to send test email" });
        } finally {
            setSendingTest(false);
        }
    };

    // =========================================================================
    // Send Campaign
    // =========================================================================
    const handleSend = async () => {
        setShowConfirm(false);
        setSending(true);
        try {
            const res = await authenticatedFetch(`${apiUrl}/api/admin/email/send`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    user_id: user?.id,
                    subject: subject.trim(),
                    body_markdown: bodyMarkdown.trim(),
                    filters: {
                        subscription_status: subscriptionStatus,
                        activity_recency: activityRecency,
                        signup_date_from: signupFrom,
                        signup_date_to: signupTo,
                    },
                }),
            });
            if (res.ok) {
                const data = await res.json();
                setActiveCampaignId(data.campaign_id);
                setActiveCampaign({
                    id: data.campaign_id,
                    subject: subject.trim(),
                    body_markdown: bodyMarkdown.trim(),
                    body_html: null,
                    filters: {},
                    recipient_count: data.recipient_count,
                    sent_count: 0,
                    failed_count: 0,
                    status: "sending",
                    sent_by: user?.id || "",
                    created_at: new Date().toISOString(),
                    completed_at: null,
                });
                setShowProgress(true);
            } else {
                const err = await res.json();
                setToast({ type: "error", message: err.error || "Send failed" });
            }
        } catch (err) {
            setToast({ type: "error", message: "Failed to start campaign" });
        } finally {
            setSending(false);
        }
    };

    // =========================================================================
    // Poll Progress
    // =========================================================================
    useEffect(() => {
        if (!showProgress || !activeCampaignId) return;

        const poll = async () => {
            try {
                const res = await authenticatedFetch(
                    `${apiUrl}/api/admin/email/campaigns/${activeCampaignId}?user_id=${user?.id}`
                );
                if (res.ok) {
                    const data: Campaign = await res.json();
                    setActiveCampaign(data);
                    if (data.status !== "sending") {
                        if (pollRef.current) clearInterval(pollRef.current);
                    }
                }
            } catch (err) {
                console.error("Poll error", err);
            }
        };

        poll();
        pollRef.current = setInterval(poll, 2000);

        return () => {
            if (pollRef.current) clearInterval(pollRef.current);
        };
    }, [showProgress, activeCampaignId, apiUrl, user?.id]);

    const handleProgressClose = () => {
        setShowProgress(false);
        setActiveCampaignId(null);
        setActiveCampaign(null);
        // Clear form and switch to history
        setSubject("");
        setBodyMarkdown("");
        setSubscriptionStatus("all");
        setActivityRecency("all");
        setSignupFrom("");
        setSignupTo("");
        setActiveTab("history");
        fetchHistory(1);
    };

    // =========================================================================
    // History
    // =========================================================================
    const fetchHistory = useCallback(async (page: number) => {
        if (!user?.id) return;
        setLoadingHistory(true);
        try {
            const params = new URLSearchParams({
                user_id: user.id,
                page: String(page),
                per_page: "10",
            });
            const res = await authenticatedFetch(`${apiUrl}/api/admin/email/campaigns?${params}`);
            if (res.ok) {
                const data = await res.json();
                setCampaigns(data.items);
                setHistoryPage(data.page);
                setHistoryPages(data.pages);
                setHistoryTotal(data.total);
            }
        } catch (err) {
            console.error("Failed to fetch campaigns", err);
        } finally {
            setLoadingHistory(false);
        }
    }, [user?.id, apiUrl]);

    useEffect(() => {
        if (activeTab === "history") {
            fetchHistory(historyPage);
        }
    }, [activeTab, historyPage, fetchHistory]);

    // =========================================================================
    // Toast auto-dismiss
    // =========================================================================
    useEffect(() => {
        if (!toast) return;
        const t = setTimeout(() => setToast(null), 5000);
        return () => clearTimeout(t);
    }, [toast]);

    // =========================================================================
    // Helpers
    // =========================================================================
    const formatDate = (iso: string) => {
        return new Date(iso).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const formatFilterSummary = (filters: Record<string, string>) => {
        const parts: string[] = [];
        const sub = filters.subscription_status;
        if (sub && sub !== "all") parts.push(`Subscription: ${SUBSCRIPTION_LABELS[sub] || sub}`);
        const act = filters.activity_recency;
        if (act && act !== "all") parts.push(`Activity: ${ACTIVITY_LABELS[act] || act}`);
        if (filters.signup_date_from) parts.push(`From: ${filters.signup_date_from}`);
        if (filters.signup_date_to) parts.push(`To: ${filters.signup_date_to}`);
        return parts.length > 0 ? parts.join(" · ") : "All users";
    };

    const canSend = subject.trim() && bodyMarkdown.trim() && recipientInfo && recipientInfo.count > 0;

    // =========================================================================
    // Render
    // =========================================================================
    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h1>Email Campaigns</h1>
            </div>

            {/* Toast */}
            {toast && (
                <div className={`${styles.toast} ${toast.type === "success" ? styles.toastSuccess : styles.toastError}`}>
                    {toast.message}
                </div>
            )}

            {/* Tabs */}
            <div className={styles.tabs}>
                <button
                    className={`${styles.tab} ${activeTab === "compose" ? styles.tabActive : ""}`}
                    onClick={() => setActiveTab("compose")}
                >
                    Compose
                </button>
                <button
                    className={`${styles.tab} ${activeTab === "history" ? styles.tabActive : ""}`}
                    onClick={() => setActiveTab("history")}
                >
                    History{historyTotal > 0 ? ` (${historyTotal})` : ""}
                </button>
            </div>

            {/* ============================================================= */}
            {/* COMPOSE TAB */}
            {/* ============================================================= */}
            {activeTab === "compose" && (
                <>
                    {/* Subject */}
                    <div className={styles.formGroup}>
                        <label>Subject</label>
                        <input
                            className={styles.input}
                            type="text"
                            placeholder="Email subject line..."
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            disabled={sending}
                        />
                    </div>

                    {/* Body */}
                    <div className={styles.formGroup}>
                        <label>Body (Markdown)</label>
                        <textarea
                            className={styles.textarea}
                            placeholder="Write your email content in Markdown..."
                            rows={12}
                            value={bodyMarkdown}
                            onChange={(e) => setBodyMarkdown(e.target.value)}
                            disabled={sending}
                        />
                    </div>

                    {/* Action buttons */}
                    <div className={styles.actionsRow}>
                        <button
                            className={styles.secondaryButton}
                            onClick={handlePreview}
                            disabled={!subject.trim() || !bodyMarkdown.trim() || loadingPreview || sending}
                        >
                            {loadingPreview ? "Loading..." : "Preview Email"}
                        </button>
                        <button
                            className={styles.secondaryButton}
                            onClick={handleSendTest}
                            disabled={!subject.trim() || !bodyMarkdown.trim() || sendingTest || sending}
                        >
                            {sendingTest ? "Sending..." : "Send Test to Me"}
                        </button>
                    </div>

                    {/* Filters */}
                    <div className={styles.filterSection}>
                        <h3>Recipient Filters</h3>
                        <div className={styles.filterGrid}>
                            <div className={styles.formGroup}>
                                <label>Subscription</label>
                                <select
                                    className={styles.select}
                                    value={subscriptionStatus}
                                    onChange={(e) => setSubscriptionStatus(e.target.value)}
                                    disabled={sending}
                                >
                                    <option value="all">All</option>
                                    <option value="active_any">Any Active</option>
                                    <option value="active_premium">Active Premium</option>
                                    <option value="active_premium_plus">Active Premium+</option>
                                    <option value="none">No Subscription</option>
                                    <option value="canceled">Canceled</option>
                                </select>
                            </div>
                            <div className={styles.formGroup}>
                                <label>Activity</label>
                                <select
                                    className={styles.select}
                                    value={activityRecency}
                                    onChange={(e) => setActivityRecency(e.target.value)}
                                    disabled={sending}
                                >
                                    <option value="all">All</option>
                                    <option value="7d">Last 7 days</option>
                                    <option value="30d">Last 30 days</option>
                                    <option value="90d">Last 90 days</option>
                                    <option value="inactive">Inactive (90+ days)</option>
                                </select>
                            </div>
                            <div className={styles.formGroup}>
                                <label>Signed up from</label>
                                <input
                                    className={styles.dateInput}
                                    type="date"
                                    value={signupFrom}
                                    onChange={(e) => setSignupFrom(e.target.value)}
                                    disabled={sending}
                                />
                            </div>
                            <div className={styles.formGroup}>
                                <label>Signed up to</label>
                                <input
                                    className={styles.dateInput}
                                    type="date"
                                    value={signupTo}
                                    onChange={(e) => setSignupTo(e.target.value)}
                                    disabled={sending}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Recipient count badge */}
                    <div className={styles.recipientBadge}>
                        {loadingCount ? (
                            <span>Loading recipients...</span>
                        ) : recipientInfo ? (
                            <>
                                <span className={styles.recipientCount}>{recipientInfo.count} users</span>
                                <span>match</span>
                                {recipientInfo.sample.length > 0 && (
                                    <span className={styles.recipientSample}>
                                        {recipientInfo.sample.join(", ")}
                                        {recipientInfo.count > 5 ? ", ..." : ""}
                                    </span>
                                )}
                            </>
                        ) : (
                            <span>-</span>
                        )}
                    </div>

                    {/* Send button */}
                    <button
                        className={styles.primaryButton}
                        onClick={() => setShowConfirm(true)}
                        disabled={!canSend || sending}
                    >
                        {sending
                            ? "Sending..."
                            : `Send to ${recipientInfo?.count ?? 0} Recipients`}
                    </button>
                </>
            )}

            {/* ============================================================= */}
            {/* HISTORY TAB */}
            {/* ============================================================= */}
            {activeTab === "history" && (
                <>
                    {loadingHistory ? (
                        <div className={styles.loading}>Loading campaigns...</div>
                    ) : campaigns.length === 0 ? (
                        <div className={styles.emptyState}>
                            <p>No campaigns sent yet.</p>
                        </div>
                    ) : (
                        <>
                            <div className={styles.campaignList}>
                                {campaigns.map((c) => (
                                    <div
                                        key={c.id}
                                        className={styles.campaignCard}
                                        onClick={() =>
                                            setExpandedCampaign(expandedCampaign === c.id ? null : c.id)
                                        }
                                    >
                                        <div className={styles.campaignCardHeader}>
                                            <h3 className={styles.campaignSubject}>{c.subject}</h3>
                                            <span
                                                className={styles.statusBadge}
                                                style={{ background: STATUS_COLORS[c.status] || "#937663" }}
                                            >
                                                {c.status}
                                            </span>
                                        </div>
                                        <div className={styles.campaignMeta}>
                                            <span>{formatDate(c.created_at)}</span>
                                            <span>{c.recipient_count} recipients</span>
                                            <span>{c.sent_count} sent</span>
                                            {c.failed_count > 0 && <span>{c.failed_count} failed</span>}
                                        </div>

                                        {expandedCampaign === c.id && (
                                            <div className={styles.campaignExpanded}>
                                                <div className={styles.campaignBody}>
                                                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                                        {c.body_markdown}
                                                    </ReactMarkdown>
                                                </div>
                                                <div className={styles.campaignFilters}>
                                                    Filters: {formatFilterSummary(c.filters || {})}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>

                            {historyPages > 1 && (
                                <div className={styles.pagination}>
                                    <button
                                        disabled={historyPage <= 1}
                                        onClick={() => setHistoryPage((p) => p - 1)}
                                    >
                                        Previous
                                    </button>
                                    <span>
                                        Page {historyPage} of {historyPages}
                                    </span>
                                    <button
                                        disabled={historyPage >= historyPages}
                                        onClick={() => setHistoryPage((p) => p + 1)}
                                    >
                                        Next
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </>
            )}

            {/* ============================================================= */}
            {/* PREVIEW MODAL */}
            {/* ============================================================= */}
            {showPreview && (
                <div className={styles.modalOverlay} onClick={() => setShowPreview(false)}>
                    <div
                        className={`${styles.modal} ${styles.previewModal}`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className={styles.modalHeader}>
                            <h2>Email Preview</h2>
                            <button className={styles.closeButton} onClick={() => setShowPreview(false)}>
                                &times;
                            </button>
                        </div>
                        <div className={styles.modalBody}>
                            <iframe
                                className={styles.previewIframe}
                                srcDoc={previewHtml}
                                title="Email Preview"
                                sandbox=""
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* ============================================================= */}
            {/* CONFIRM MODAL */}
            {/* ============================================================= */}
            {showConfirm && (
                <div className={styles.modalOverlay} onClick={() => setShowConfirm(false)}>
                    <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h2>Confirm Send</h2>
                            <button className={styles.closeButton} onClick={() => setShowConfirm(false)}>
                                &times;
                            </button>
                        </div>
                        <div className={styles.modalBody}>
                            <div className={styles.confirmDetails}>
                                <div className={styles.confirmRow}>
                                    <span className={styles.confirmLabel}>Subject:</span>
                                    <span className={styles.confirmValue}>{subject}</span>
                                </div>
                                <div className={styles.confirmRow}>
                                    <span className={styles.confirmLabel}>Recipients:</span>
                                    <span className={styles.confirmValue}>{recipientInfo?.count ?? 0} users</span>
                                </div>
                                {recipientInfo && recipientInfo.sample.length > 0 && (
                                    <div className={styles.confirmRow}>
                                        <span className={styles.confirmLabel}>Sample:</span>
                                        <span className={styles.confirmValue}>
                                            {recipientInfo.sample.join(", ")}
                                            {recipientInfo.count > 5 ? ", ..." : ""}
                                        </span>
                                    </div>
                                )}
                                <div className={styles.confirmRow}>
                                    <span className={styles.confirmLabel}>Filters:</span>
                                    <span className={styles.confirmValue}>
                                        {formatFilterSummary({
                                            subscription_status: subscriptionStatus,
                                            activity_recency: activityRecency,
                                            signup_date_from: signupFrom,
                                            signup_date_to: signupTo,
                                        })}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className={styles.modalFooter}>
                            <button className={styles.secondaryButton} onClick={() => setShowConfirm(false)}>
                                Cancel
                            </button>
                            <button className={styles.primaryButton} onClick={handleSend}>
                                Send Now
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ============================================================= */}
            {/* PROGRESS MODAL */}
            {/* ============================================================= */}
            {showProgress && activeCampaign && (
                <div className={styles.modalOverlay}>
                    <div
                        className={`${styles.modal} ${styles.progressModal}`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className={styles.modalHeader}>
                            <h2>Sending Campaign</h2>
                            {activeCampaign.status !== "sending" && (
                                <button className={styles.closeButton} onClick={handleProgressClose}>
                                    &times;
                                </button>
                            )}
                        </div>
                        <div className={styles.modalBody}>
                            <div className={styles.progressContainer}>
                                <div className={styles.progressText}>
                                    {activeCampaign.status === "sending"
                                        ? `Sending ${activeCampaign.sent_count + activeCampaign.failed_count} / ${activeCampaign.recipient_count} emails...`
                                        : activeCampaign.status === "completed"
                                        ? "All emails sent successfully!"
                                        : activeCampaign.status === "partial"
                                        ? "Campaign completed with some failures"
                                        : "Campaign failed"}
                                </div>
                                <div className={styles.progressBarOuter}>
                                    <div
                                        className={styles.progressBarInner}
                                        style={{
                                            width: `${
                                                activeCampaign.recipient_count > 0
                                                    ? Math.round(
                                                          ((activeCampaign.sent_count + activeCampaign.failed_count) /
                                                              activeCampaign.recipient_count) *
                                                              100
                                                      )
                                                    : 0
                                            }%`,
                                            background:
                                                activeCampaign.status === "completed"
                                                    ? "#15803d"
                                                    : activeCampaign.status === "failed"
                                                    ? "#ba5656"
                                                    : activeCampaign.status === "partial"
                                                    ? "#c2410c"
                                                    : undefined,
                                        }}
                                    />
                                </div>
                                <div className={styles.progressStats}>
                                    {activeCampaign.sent_count} sent
                                    {activeCampaign.failed_count > 0 &&
                                        ` · ${activeCampaign.failed_count} failed`}
                                    {" · "}
                                    {activeCampaign.recipient_count} total
                                </div>
                            </div>
                        </div>
                        {activeCampaign.status !== "sending" && (
                            <div className={styles.modalFooter}>
                                <button className={styles.primaryButton} onClick={handleProgressClose}>
                                    Done
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminEmail;
